import React from 'react'
import { useStaticQuery, graphql, Link } from 'gatsby'
import {
  getConferenceLocation,
  getConferenceVenueType,
  getEventLink,
  setColorTheme,
} from 'theme/utils/generic-helpers'
import { startAndEndDate } from 'src/utils/date-helpers'

import UXDXLogo from 'src/images/uxdx-logo.svg'
import emeaCityScape from 'src/images/conferenceLocations/dublin.svg'
import apacCityScape from 'src/images/conferenceLocations/singapore.svg'
import usaCityScape from 'src/images/conferenceLocations/newyork.svg'

const getCityScape = (conferenceLocation) => {
  switch (conferenceLocation) {
    case 'emea':
      return emeaCityScape
    case 'apac':
      return apacCityScape
    case 'usa':
      return usaCityScape
    default:
      return usaCityScape
  }
}

const UpcomingConferences = () => {
  const { futureEvents } = useStaticQuery(query)
  return (
    <div className="sm:px-6 lg:px-8 flex sm:block items-center">
      <div className="max-w-md mx-auto md:max-w-7xl sm:flex sm:justify-center gap-16 lg:gap-32 sm:flex-wrap">
        {futureEvents &&
          futureEvents.nodes.map((conference) => {
            const conferenceLocation = getConferenceLocation(conference)
            return (
              <Link
                to={getEventLink(conference.data.Conference_Id, conferenceLocation, 'agenda')}
                key={conference.data.Conference_Id}
                className={`flex flex-col rounded-lg shadow-lg overflow-hidden w-80 mb-6 xl:mb-0 transform hover:scale-105 ease-in-out duration-300 sm:bg-gradient-to-tr sm:from-uxdx-700
                ${setColorTheme(
                  'bg-apac-700 sm:to-apac-700 bg-emea-700 sm:to-emea-700 bg-usa-700 sm:to-usa-700 bg-community-700 sm:via-community-500 sm:to-community-700',
                  conferenceLocation,
                )}`}
              >
                <div className="px-6 py-8 sm:p-10 sm:pb-6 bg-usaCityScape">
                  <div className="flex items-center ">
                    <img className="h-10 w-10" src={UXDXLogo} alt="UXDX logo" />
                    <p className="text-3xl pt-1 font-uxdx text-white font-bold ml-2">UXDX</p>
                  </div>
                  <h3 className="mt-4 flex items-baseline text-4xl leading-none font-extrabold text-white font-uxdx">
                    {conference.data.Location} {conference.data.Year}
                  </h3>
                  <p className="mt-4 text-lg leading-7 text-gray-100">
                    {startAndEndDate(
                      conference.data.Conference_Start_Date,
                      conference.data.Conference_End_Date,
                      'UTC',
                    )}
                  </p>
                  <p className="mt-1 text-sm leading-7 text-gray-200">
                    {getConferenceVenueType(
                      conference.data.Venue,
                      conference.data.Conference_Venue_Type,
                      true,
                    )}
                  </p>
                </div>
                <div className="h-20 flex flex-col justify-end">
                  <img src={getCityScape(conferenceLocation)} className="w-full" />
                </div>
                <div className="flex-1 flex flex-col justify-between px-6 pt-6 pb-4 space-y-6 sm:p-10 sm:pt-6">
                  <ul className="space-y-4">
                    {conference.data.Stats &&
                      conference.data.Stats.map((stat) => {
                        return (
                          <li key={stat.id} className="flex items-start">
                            <div className="flex-shrink-0">
                              <svg
                                className="h-6 w-6 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                            <p className="ml-3 text-base leading-6 text-gray-50">
                              {stat.data.Stat} {stat.data.Value}
                            </p>
                          </li>
                        )
                      })}
                    <li className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-6 w-6 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <p className="ml-3 text-base leading-6 text-gray-50"> Product, UX, Design, &amp; Dev</p>
                    </li>
                  </ul>
                  <div className="rounded-md shadow">
                    <button
                      className={`${setColorTheme(
                        'bg-uxdx-500 hover:bg-uxdx-600 bg-emea-500 hover:bg-emea-600 bg-apac-500 hover:bg-apac-600 bg-usa-500 hover:bg-usa-600',
                        conferenceLocation,
                      )} w-full flex items-center justify-center px-5 py-3 border border-transparent text-base leading-6 font-medium rounded-md text-white focus:outline-none focus:ring transition duration-150 ease-in-out`}
                      aria-describedby="tier-standard"
                    >
                      View Agenda
                    </button>
                  </div>
                </div>
              </Link>
            )
          })}
      </div>
    </div>
  )
}

const query = graphql`
  {
    futureEvents: allAirtableConference(
      filter: {
        data: {
          Type: { eq: "Conference" }
          Conference_Status: { eq: "Published" }
          Organisation: { eq: "UXDX" }
          Future_Conference: { eq: "FUTURE" }
        }
      }
      sort: { data: { Conference_Start_Date: ASC } }
    ) {
      nodes {
        data {
          Conference_Id
          Conference_Name
          Conference_Start_Date
          Conference_End_Date
          Public_Release_Date
          Type
          Location
          Year
          Session_Count: _xSessions
          Stats {
            id
            data {
              Stat
              Value
            }
          }
          Conference_Venue_Type
          Venue {
            data {
              Name
              Venue_Name
              Venue_City
              Venue_Address
              Venue_Type
              Country
              Post_Code
            }
          }
        }
      }
    }
  }
`
export default UpcomingConferences
